import { storage as localStorage } from './storage';
import { User, RegistrationRequest } from '../types';
import { auth, db, storage as firebaseStorage, isFirebaseConfigured } from './firebaseConfig';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { systemLogService } from './systemLogService';

const KEYS = {
    USERS: 'sbaj_users',
    REQUESTS: 'sbaj_registration_requests',
    SESSION: 'sbaj_user'
};

// Simple mock hash function (NOT for real production security, but better than plain text for demo)
const hashPassword = (password: string) => btoa(password).split('').reverse().join('');

export const authService = {
    // --- User Management ---
    getUsers: async (): Promise<User[]> => {
        if (isFirebaseConfigured() && db) {
            try {
                const querySnapshot = await getDocs(collection(db, 'users'));
                return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
            } catch (error) {
                console.error('Error fetching users from Firebase:', error);
                return [];
            }
        }

        // Mock Fallback
        await localStorage.delay(200);
        let users = localStorage.get<User[]>(KEYS.USERS, []);

        // Auto-seed if empty
        const demoUserEmail = 'demo@sbaj.org.jm';
        const hasDemoUser = users.some(u => u.email === demoUserEmail);

        if (users.length === 0 || !hasDemoUser) {
            const { mockUsers, mockSbajMembers } = await import('../data/mockData');
            const seededUsers = Object.entries(mockUsers).map(([email, data]: [string, any]) => {
                const member = mockSbajMembers[data.membershipId];
                return {
                    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                    name: data.role === 'superadmin' ? 'Super Admin' : data.role === 'admin' ? 'Admin User' : 'Regular User',
                    email,
                    role: data.role,
                    companyName: member?.companyName || 'SBAJ Member',
                    membershipId: data.membershipId,
                    contactPerson: member?.contactPerson || 'System User',
                    businessType: member?.businessType || 'Internal',
                    _passwordHash: hashPassword(data.password)
                };
            });
            localStorage.set(KEYS.USERS, seededUsers);
            users = seededUsers;
        }

        return users;
    },

    getUserProfile: async (userId: string): Promise<User | undefined> => {
        if (isFirebaseConfigured() && db) {
            try {
                const userDoc = await getDoc(doc(db, 'users', userId));
                if (userDoc.exists()) {
                    return { id: userDoc.id, ...userDoc.data() } as User;
                }
            } catch (error) {
                console.error('Error fetching user profile:', error);
            }
        }

        // Mock / Fallback
        const users = await authService.getUsers();
        return users.find(u => u.id === userId);
    },

    createUser: async (user: User, password?: string): Promise<User> => {
        if (isFirebaseConfigured() && auth && db && password) {
            try {
                const userCredential = await createUserWithEmailAndPassword(auth, user.email, password);
                const newUser = { ...user, id: userCredential.user.uid };
                await setDoc(doc(db, 'users', userCredential.user.uid), newUser);
                return newUser;
            } catch (error) {
                console.error('Error creating user in Firebase:', error);
                throw error;
            }
        }

        // Mock Fallback
        await localStorage.delay(500);
        const users = await authService.getUsers(); // Use getUsers to ensure seeding
        // In a real app, password would be hashed and stored in a separate auth table
        // Here we attach it to the user object for simplicity of the simulation
        const newUser = { ...user, _passwordHash: password ? hashPassword(password) : undefined };
        const updatedUsers = [...users, newUser];
        localStorage.set(KEYS.USERS, updatedUsers);
        return user;
    },

    updateUser: async (user: User): Promise<User> => {
        if (isFirebaseConfigured() && db) {
            try {
                // Ensure ID exists
                if (!user.id) throw new Error("User ID is required for update.");

                // Remove undefined fields if any (Sanitize)
                const sanitizedUser = JSON.parse(JSON.stringify(user));

                await setDoc(doc(db, 'users', user.id), sanitizedUser, { merge: true });
                return user; // Return the updated object
            } catch (error) {
                console.error("Error updating user in Firebase:", error);
                throw error;
            }
        }

        await localStorage.delay(400);
        const users = await authService.getUsers();
        const updatedUsers = users.map(u => u.id === user.id ? { ...u, ...user } : u);
        localStorage.set(KEYS.USERS, updatedUsers);

        // Update session if it's the current user
        const currentUser = localStorage.get<User | null>(KEYS.SESSION, null);
        if (currentUser && currentUser.id === user.id) {
            localStorage.set(KEYS.SESSION, { ...currentUser, ...user });
        }

        return user;
    },

    uploadProfileImage: async (userId: string, file: File): Promise<string> => {
        if (isFirebaseConfigured() && firebaseStorage && auth) {
            try {
                const storageRef = ref(firebaseStorage, `users/${userId}/avatar_${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                return downloadURL;
            } catch (error) {
                console.error('Error uploading profile image to Firebase:', error);
                throw error;
            }
        }

        // Mock Fallback
        await localStorage.delay(1000);
        // Return a fake URL or a data URL if we wanted to be fancy, but for now just a placeholder or the object URL
        return URL.createObjectURL(file);
    },

    // --- Authentication ---
    login: async (email: string, password: string): Promise<User> => {
        if (isFirebaseConfigured() && auth && db) {
            try {
                const userCredential = await signInWithEmailAndPassword(auth, email, password);

                // Enforce Email Verification
                if (!userCredential.user.emailVerified) {
                    await signOut(auth);
                    throw new Error('Please verify your email address before logging in.');
                }

                const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data() as User;
                    const loggedInUser = { ...userData, id: userCredential.user.uid };

                    if (loggedInUser.status === 'suspended') {
                        await signOut(auth);
                        throw new Error('Your account has been suspended. Please contact support.');
                    }

                    if (loggedInUser.status === 'pending') {
                        await signOut(auth);
                        throw new Error('Your account is pending approval. Please wait for an administrator to review your registration.');
                    }

                    if (loggedInUser.status === 'rejected') {
                        await signOut(auth);
                        throw new Error('Your registration request has been rejected. Please contact support.');
                    }

                    localStorage.set(KEYS.SESSION, loggedInUser); // Store in mock session for consistency
                    systemLogService.addLog('success', 'User Login Success', `User: ${loggedInUser.email} (Firebase)`);
                    return loggedInUser;
                }

                // If user exists in Auth but not Firestore, it usually means approval hasn't happened yet 
                // or creation failed. Safe to block.
                await signOut(auth);
                throw new Error('Account profile not found. If you recently registered, your account might still be pending approval.');
            } catch (error: any) {
                // If it's a custom logical error (no Firebase error code) or a specific policy violation, rethrow it
                // This ensures "Pending Approval" or "Verify Email" messages are shown to the user
                if (!error.code ||
                    error.message.includes('verify your email') ||
                    error.message.includes('account is pending') ||
                    error.message.includes('suspended') ||
                    error.message.includes('rejected') ||
                    error.message.includes('Account profile not found')
                ) {
                    throw error;
                }

                console.warn('Firebase login failed, attempting mock fallback:', error);
                systemLogService.addLog('warning', 'Firebase Login Failed', `Error: ${error.message}`);
                // Fall through to mock logic
            }
        }

        // Mock Fallback
        await localStorage.delay(800);
        const users = await authService.getUsers(); // Ensure users are loaded/seeded

        const user = users.find(u => u.email === email && (u as any)._passwordHash === hashPassword(password));

        if (!user) {
            systemLogService.addLog('error', 'Login Failed', `Invalid credentials for ${email}`);
            throw new Error('Invalid credentials');
        }

        if (user.status === 'suspended') {
            throw new Error('Your account has been suspended. Please contact support.');
        }

        // Remove hash before returning/storing session
        const { _passwordHash, ...safeUser } = user as any;
        localStorage.set(KEYS.SESSION, safeUser);

        // Send login alert
        const { emailService } = await import('./emailService');
        emailService.sendLoginAlert(safeUser.email, new Date().toLocaleString(), 'Web Browser');

        systemLogService.addLog('success', 'User Login Success', `User: ${safeUser.email}`);
        return safeUser;
    },

    logout: async (): Promise<void> => {
        if (isFirebaseConfigured() && auth) {
            await signOut(auth);
        }
        await localStorage.delay(200); // Keep delay for mock consistency
        localStorage.remove(KEYS.SESSION);
        systemLogService.addLog('info', 'User Logout', 'Session ended');
    },

    getCurrentUser: (): User | null => {
        // For Firebase, auth state is handled via listeners, but we can check local storage for cached session
        // or rely on the AuthContext to handle the listener.
        // This method is primarily for synchronous access to stored session.
        return localStorage.get<User | null>(KEYS.SESSION, null);
    },

    // --- Registration Requests ---
    getRegistrationRequests: async (): Promise<RegistrationRequest[]> => {
        if (isFirebaseConfigured() && db) {
            try {
                const querySnapshot = await getDocs(collection(db, 'registration_requests'));
                return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as RegistrationRequest));
            } catch (error) {
                console.error('Error fetching requests from Firebase:', error);
                return [];
            }
        }

        // Mock Fallback
        await localStorage.delay(300);
        return localStorage.get<RegistrationRequest[]>(KEYS.REQUESTS, []);
    },

    submitRegistrationRequest: async (request: RegistrationRequest): Promise<void> => {
        if (isFirebaseConfigured() && db && auth) {
            try {
                // 1. Create User in Firebase Auth
                const userCredential = await createUserWithEmailAndPassword(auth, request.email, request.password!);
                const user = userCredential.user;

                // 2. Send Verification Email
                await sendEmailVerification(user);

                // 3. Prepare data for Firestore (remove password for security)
                const sanitizedRequest = JSON.parse(JSON.stringify(request));
                if (sanitizedRequest.password) delete sanitizedRequest.password;

                const requestData = {
                    ...sanitizedRequest,
                    userId: user.uid,
                    status: 'pending',
                    emailVerified: false,
                    requestedAt: new Date().toISOString()
                };

                // 4. Save to Firestore (Use UID as Document ID to link them easily)
                const docRef = doc(db, 'registration_requests', user.uid);
                console.log(`[AuthService] Creating request at: ${docRef.path}`, requestData);
                await setDoc(docRef, requestData);

                // 5. Create Initial User Profile (Pending)
                // This ensures the user profile exists immediately, but with 'pending' status
                const userProfile: User = {
                    id: user.uid,
                    companyName: request.companyName,
                    membershipId: request.membershipId,
                    contactPerson: request.contactPerson,
                    email: request.email,
                    phone: request.phone,
                    businessType: request.businessType,
                    role: 'member',
                    status: 'pending', // Pending Admin Approval
                    verified: false,
                    name: request.contactPerson
                };
                await setDoc(doc(db, 'users', user.uid), userProfile);

                // 6. Sign out immediately so they don't stay logged in as a "pending" user
                await signOut(auth);

                systemLogService.addLog('info', 'Registration Request Submitted', `Company: ${request.companyName}`);
                return;
            } catch (error: any) {
                console.error('[AuthService] Error submitting request to Firebase:', error);

                if (error.code === 'permission-denied') {
                    console.error('PERMISSION DENIED: Check Firestore Rules.');
                }

                throw error;
            }
        }

        // Mock Fallback
        await localStorage.delay(600); // Adjusted delay for consistency
        const requests = await authService.getRegistrationRequests();
        const verificationToken = Math.random().toString(36).substring(2) + Date.now().toString(36);

        const newRequest = {
            ...request,
            id: Date.now().toString(),
            status: 'pending',
            requestedAt: new Date().toISOString(),
            emailVerified: false,
            verificationToken
        }; // Ensure requestedAt is string

        localStorage.set(KEYS.REQUESTS, [...requests, newRequest]);
        systemLogService.addLog('info', 'Registration Request Submitted', `Company: ${request.companyName}`);

        // Send Verification Email
        const { emailService } = await import('./emailService');
        await emailService.sendVerificationEmail(request.email, verificationToken);
    },

    verifyEmail: async (token: string): Promise<boolean> => {
        await localStorage.delay(500);
        const requests = await authService.getRegistrationRequests();
        const request = requests.find(r => r.verificationToken === token);

        if (request) {
            const updatedRequests = requests.map(r => r.id === request.id ? { ...r, emailVerified: true } : r);
            localStorage.set(KEYS.REQUESTS, updatedRequests);
            return true;
        }
        return false;
    },

    approveRequest: async (request: RegistrationRequest): Promise<void> => {
        if (isFirebaseConfigured() && db && auth) {
            try {
                let uid = (request as any).userId;

                // Robustness: In the new flow, request.id IS the User ID (uid).
                // If userId field is missing (for whatever reason), but request.id looks like a valid UID, use it.
                // Firebase UIDs are typically 28 chars.
                if (!uid && request.id && request.id.length > 20) {
                    uid = request.id;
                }

                // Legacy: If no userId linked, create the user now
                // Also handles case where userId might be missing but user exists (New Flow glitch protection)
                if (!uid && request.password) {
                    try {
                        const userCredential = await createUserWithEmailAndPassword(auth, request.email, request.password);
                        uid = userCredential.user.uid;
                    } catch (err: any) {
                        if (err.code === 'auth/email-already-in-use') {
                            // User exists but we didn't have the UID upfront.
                            // If we fallback to request.id, we risk creating a duplicate profile if request.id != uid.
                            // Better to fail and ask for a fresh registration if this is a legacy/broken request.
                            throw new Error('User already exists but request is missing User ID. Cannot approve securely.');
                        } else {
                            throw err;
                        }
                    }
                }

                if (!uid) {
                    // Fallback: If request.id looks like a UID (new flow), try using it
                    // But usually we stored userId.
                    // If we can't find UID, we can't create the profile.
                    // Assuming request.id IS the uid in the new flow.
                    if (request.id) uid = request.id;
                    else throw new Error("Cannot approve request: Missing User ID.");
                }

                // Create user profile (Active Member)
                const newUser: User = {
                    id: uid,
                    companyName: request.companyName,
                    membershipId: request.membershipId,
                    contactPerson: request.contactPerson,
                    email: request.email,
                    phone: request.phone,
                    businessType: request.businessType,
                    role: 'member',
                    status: 'approved', // Active now
                    verified: true,
                    name: request.contactPerson
                };

                await setDoc(doc(db, 'users', uid), newUser, { merge: true });

                // Update request status
                if (request.id) {
                    await setDoc(doc(db, 'registration_requests', request.id), { status: 'approved' }, { merge: true });
                }

                systemLogService.addLog('success', 'Registration Request Approved', `User: ${request.email}`);
                return;
            } catch (error) {
                console.error('Error approving request in Firebase:', error);
                throw error;
            }
        }

        // Mock Fallback
        await localStorage.delay(500);
        const requests = await authService.getRegistrationRequests();
        const updatedRequests = requests.map(r => r.id === request.id ? { ...r, status: 'approved' } : r);
        localStorage.set(KEYS.REQUESTS, updatedRequests);

        // Create the user
        const newUser: User = {
            ...request,
            id: request.id || Date.now().toString(), // Ensure ID for mock user
            role: 'member',
            status: 'approved',
            verified: true
        };
        const createdUser = await authService.createUser(newUser, request.password);

        // Send welcome notification
        const { notificationService } = await import('./notificationService');
        notificationService.createNotification({
            userId: createdUser.id!,
            title: 'Registration Approved',
            message: 'Welcome to SBAJ Cybersecurity Portal! Your account has been approved.',
            type: 'success',
            link: '/dashboard'
        });

        // Send welcome email
        const { emailService } = await import('./emailService');
        emailService.sendWelcomeEmail(createdUser.email, createdUser.name ?? createdUser.contactPerson ?? createdUser.email);
        systemLogService.addLog('success', 'Registration Request Approved', `User: ${createdUser.email}`);
    },

    rejectRequest: async (requestId: string): Promise<void> => {
        if (isFirebaseConfigured() && db) {
            try {
                await setDoc(doc(db, 'registration_requests', requestId), { status: 'rejected' }, { merge: true });
                systemLogService.addLog('warning', 'Registration Request Rejected', `Request ID: ${requestId}`);
                return;
            } catch (error) {
                console.error('Error rejecting request in Firebase:', error);
                throw error;
            }
        }

        // Mock Fallback
        await localStorage.delay(300);
        const requests = await authService.getRegistrationRequests();
        const updatedRequests = requests.map(r => r.id === requestId ? { ...r, status: 'rejected' } : r);
        localStorage.set(KEYS.REQUESTS, updatedRequests);
        systemLogService.addLog('warning', 'Registration Request Rejected', `Request ID: ${requestId}`);
    },

    // --- Password Reset ---
    resetPassword: async (email: string): Promise<void> => {
        if (isFirebaseConfigured() && auth) {
            try {
                await sendPasswordResetEmail(auth, email);
                systemLogService.addLog('info', 'Password Reset Requested', `Email: ${email} (Firebase)`);
                return;
            } catch (error: any) {
                console.error('Error sending password reset email via Firebase:', error);
                systemLogService.addLog('error', 'Password Reset Failed', `Email: ${email}, Error: ${error.message}`);
                // Proceed to fallback or throw error depending on desired behavior.
                // For now, if Firebase fails (e.g. user not found), we should probably let the user know or throw.
                // But typically for security we don't reveal if user exists.
                // However, Firebase throws 'auth/user-not-found'.
                throw error;
            }
        }

        // Mock Fallback
        const { emailService } = await import('./emailService');
        await emailService.sendPasswordResetEmail(email, `${window.location.origin}/reset-password?token=mock-token`);
        systemLogService.addLog('info', 'Password Reset Requested', `Email: ${email} (Mock)`);
    },

    resendVerificationEmail: async (email: string, password?: string): Promise<void> => {
        if (isFirebaseConfigured() && auth) {
            try {
                // We need to sign in to get the user object to send verification
                if (!password) throw new Error('Password required to resend verification.');

                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                if (user.emailVerified) {
                    await signOut(auth);
                    throw new Error('Email is already verified. You can log in.');
                }

                await sendEmailVerification(user);
                await signOut(auth); // Sign out again

                systemLogService.addLog('info', 'Verification Email Resent', `Email: ${email}`);
                return;
            } catch (error: any) {
                console.error('Error resending verification email:', error);

                if (error.code === 'auth/too-many-requests') {
                    throw new Error('Too many requests. Please wait a moment before trying again.');
                }

                throw error;
            }
        }

        // Mock Fallback
        const { emailService } = await import('./emailService');
        // Find user token if possible, or generate mock
        const requests = localStorage.get<RegistrationRequest[]>(KEYS.REQUESTS, []);
        const request = requests.find(r => r.email === email);
        const token = request?.verificationToken || 'mock-token';

        await emailService.sendVerificationEmail(email, token);
        systemLogService.addLog('info', 'Verification Email Resent', `Email: ${email} (Mock)`);
    }
};
